// imported from myintelli-onnx commit 9f78984 on 2026-05-04
// do not edit manually — regenerate via `node scripts/sync-onnx-vendor.mjs`
/**
 * Platform abstraction for ONNX inference. The pipeline calls into a
 * `OnnxRuntime` interface; each platform provides its own implementation:
 *
 *   • Browser (web admin, web algorithm path)  → onnxruntime-web
 *   • React Native (mobile end-user)           → onnxruntime-react-native
 *   • Node (server, tests, dev tooling)        → onnxruntime-node
 *
 * /core/js does NOT depend on any of these directly. Each app wires its own
 * provider in. This keeps the package importable in environments that don't
 * have a runtime (e.g., type-checking from /web while bundling).
 *
 * The `InferenceSession` and `Tensor` types are intentionally minimal — they
 * mirror the subset of `onnxruntime-common` we actually use, and let us mock
 * cleanly in unit tests.
 */

export interface Tensor {
  readonly type: 'float32' | 'int32' | 'uint8';
  readonly data: Float32Array | Int32Array | Uint8Array;
  readonly dims: readonly number[];
}

export interface InferenceSession {
  /** Output names in declaration order. */
  readonly outputNames: readonly string[];
  /** Input names in declaration order. */
  readonly inputNames: readonly string[];
  run(feeds: Record<string, Tensor>): Promise<Record<string, Tensor>>;
  release(): Promise<void>;
}

export interface OnnxRuntime {
  /** Load a model from a path (Node/RN) or URL/ArrayBuffer (browser). */
  createSession(
    source: string | ArrayBuffer | Uint8Array,
    options?: { executionProviders?: string[] },
  ): Promise<InferenceSession>;
  createTensor(data: Float32Array | Int32Array | Uint8Array, dims: readonly number[]): Tensor;
}

let _runtime: OnnxRuntime | null = null;

export function setRuntime(rt: OnnxRuntime): void {
  _runtime = rt;
}

export function getRuntime(): OnnxRuntime {
  if (!_runtime) {
    throw new Error(
      'No ONNX runtime registered. Call setRuntime() at app boot — see core/js/src/platform/README.md',
    );
  }
  return _runtime;
}

/** Test helper: clear the runtime so a fresh mock can be installed. */
export function _resetRuntimeForTesting(): void {
  _runtime = null;
}
